import { reactive } from 'vue';
import { useFontStore } from '../stores/fontStore.js';
import { getFontFile } from './resourceStorage.js';

/**
 * DesignerFontLoader — Designer *rendering-layer* service, deliberately not
 * part of the Resource layer (see docs/PROJECT-RESOURCE-ARCHITECTURE-PLAN.md,
 * "Option A": a Font Resource is a source file, never tied to one size).
 *
 * Caches by Font Resource uuid ALONE — not by (uuid, size). Unlike firmware,
 * which needs a distinct generated bitmap font per pixel size, the browser's
 * FontFace/Canvas API can render one loaded face at any font-size, so a
 * widget changing its Size (px) never needs a reload. (A per-(uuid, size)
 * cache was tried first and caused a visible fallback-font flash on every
 * size change — each new size was an uncached FontFace needing a fresh
 * IndexedDB read + FontFace.load() round-trip before correcting back to the
 * real font. Caching by uuid alone means only the FIRST size a widget uses
 * ever pays that cost; every later size change reuses the same loaded face
 * instantly.)
 *
 * FontStore/FontDB know nothing about any of this — they only store and
 * retrieve font metadata and bytes. Consumers (HaspLabel, and any future
 * widget) call loadFont(uuid) and either watch `status[uuid]` reactively or
 * poll getFontFamily(uuid)/isLoaded(uuid).
 */

// uuid -> 'loading' | 'loaded' | 'error'. Reactive so callers can `watch()` a
// specific cache key and react the moment it resolves. Intentionally not
// Pinia state — this is a rendering cache, not project data, and keeping it
// out of a store underlines that FontStore itself holds none of it.
const status = reactive({});

// uuid -> FontFace, kept only so unloadFont() can remove it from
// document.fonts again.
const loadedFaces = new Map();

function familyNameFor(uuid) {
    return `hasp-font-${uuid}`;
}

/** Load a Font Resource's uploaded file, if it isn't already loading/loaded. */
async function loadFont(uuid) {
    if (!uuid) return;
    if (status[uuid] === 'loading' || status[uuid] === 'loaded') return;

    const resource = useFontStore().getFont(uuid);
    if (!resource?.storageId) return;

    status[uuid] = 'loading';
    try {
        const record = await getFontFile(resource.storageId);
        if (!record?.data) throw new Error('font file not found in storage');
        const face = new FontFace(familyNameFor(uuid), `url(${record.data})`);
        await face.load();
        document.fonts.add(face);
        loadedFaces.set(uuid, face);
        status[uuid] = 'loaded';
    } catch (e) {
        console.warn(`DesignerFontLoader: failed to load font "${resource.name}":`, e);
        status[uuid] = 'error';
    }
}

/**
 * Drop a loaded font — e.g. its file was re-uploaded (stale bytes) or the
 * resource was deleted.
 */
function unloadFont(uuid) {
    const face = loadedFaces.get(uuid);
    if (!face) return;
    document.fonts.delete(face);
    loadedFaces.delete(uuid);
    delete status[uuid];
}

function isLoaded(uuid) {
    return status[uuid] === 'loaded';
}

/** The browser font-family name for this uuid once loaded, else null (caller should keep using its own fallback until then). */
function getFontFamily(uuid) {
    return isLoaded(uuid) ? familyNameFor(uuid) : null;
}

function getStatus(uuid) {
    return status[uuid] ?? 'idle';
}

export const DesignerFontLoader = {
    status, // read directly (e.g. inside a Vue watch getter) via status[uuid]
    loadFont,
    unloadFont,
    isLoaded,
    getFontFamily,
    getStatus,
};
